import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { AzureSearchRequest, AzureSearchService } from "@rubbl-azure-search";
import { EnvironmentConfig, MarketTrackingEvents } from "@rubbl-core";
import { InventoryItem } from "@rubbl-inventory";
import { NavigationItem } from "@rubbl-layout";
import { map, forkJoin } from "rxjs";

declare const environment: EnvironmentConfig;

@Component({
  selector: "app-home-popular-categories",
  templateUrl: "./home-popular-categories.component.html",
  styleUrls: ["./home-popular-categories.component.scss"],
})
export class HomePopularCategoriesComponent implements OnInit {
  activeNavigationItem: string = "All Rentals";
  baseFilter: string =
    "(status eq 'Available' or status eq 'Onboarding') and " +
    "(rentalRate ne 0) and " +
    "(requiresAdminApproval eq false) and " + // we can get rid of this in a few days
    "(approvalStatus eq 'Approved' or approvalStatus eq null)";

  buttonText: string = "View All Rentals";
  queryParams: any = {};

  renterNavigationItem: NavigationItem[] = [
    {
      label: "All Rentals",
      click: () => {
        this.search(this.baseFilter);
        this.buttonText = "View All Rentals";
        this.queryParams = {};
      },
      active: true,
    },
    {
      label: "Buy it Now",
      click: () => {
        this.search(this.baseFilter + " and (buyItNowEnabled eq true)");
        this.buttonText = "View All Buy it Now";
        this.queryParams = { buyItNowEnabled: true, radius: "anywhere" };
      },
    },
    {
      label: "Rent to Own",
      click: () => {
        this.search(this.baseFilter + " and (rpoEnabled eq true)");
        this.buttonText = "View All Rent to Own";
        this.queryParams = { rpoEnabled: true, radius: "anywhere" };
      },
    },
  ];

  baseUrlForIcons = "https://company.rubbl.com/pubweb/marketplace/homepage/popular-categories/";

  popularMachineCategoryData = null;
  allMachineCategoryData = null;

  baseImagePath: string = environment.baseImagePath;

  constructor(private azureSearchService: AzureSearchService) {}

  ngOnInit() {
    const filter = this.search(this.baseFilter);
  }

  search(filter: string) {
    const azureSearchRequest = new AzureSearchRequest();
    azureSearchRequest.search = "*";
    azureSearchRequest.top = 0;
    azureSearchRequest.filter = filter;
    azureSearchRequest.facets = ["primaryType,count:50", "typeDefinition,count: 1000", "indexKey, count:1000, sort:value"];

    const machineInventoryIndex$ = this.azureSearchService.search<InventoryItem>(azureSearchRequest, "machines");

    azureSearchRequest.facets = null;
    azureSearchRequest.filter =
      "definitionType eq 'MachineType' and not (stockImage eq '') and ((parentTypeDefinitionCode eq '') or (parentTypeDefinitionCode eq null))";
    azureSearchRequest.top = 1000;
    azureSearchRequest.select = "name, stockImage";

    const machineTypeDefIndex$ = this.azureSearchService.search<any>(azureSearchRequest, "type-definitions");

    forkJoin([machineInventoryIndex$, machineTypeDefIndex$]).subscribe(result => {
      let inventoryPrimaryTypes = result[0].search.facets.primaryType;
      const machineTypeDefinitions = result[1].value;

      const data = [];
      const allMachineCategoryData = [];
      const types = result[0].search.facets.indexKey;

      // Sort inventoryPrimaryTypes by count in descending order
      inventoryPrimaryTypes.sort((a, b) => b.count - a.count);

      const categories = inventoryPrimaryTypes.map(type => type.value);
      const imageMapping = this.generateImageMapping(categories);

      // Iterate over sorted inventoryPrimaryTypes
      for (const inventoryPrimaryType of inventoryPrimaryTypes) {
        // Find the corresponding machine type definition
        const machineTypeDef = machineTypeDefinitions.find(m => m.name === inventoryPrimaryType.value);
        if (!machineTypeDef) {
          continue;
        }

        // Push the data for each primary type
        if (data.length < 12) {
          const dataItem = {
            header: `${inventoryPrimaryType.value}`,
            subHeader: `${inventoryPrimaryType.count}`,
            cardImgSrc: imageMapping[inventoryPrimaryType.value],
            trackingEvent: MarketTrackingEvents.Home.ExploreCardClick,
            route: `equipment-rentals/${inventoryPrimaryType.value}`,
          };

          data.push(dataItem);
        }

        allMachineCategoryData.push({
          name: `${inventoryPrimaryType.value}`,
          count: `${inventoryPrimaryType.count}`,
          cardImgSrc: this.baseImagePath + machineTypeDef.stockImage,
          trackingEvent: MarketTrackingEvents.Home.ExploreCardClick,
        });
      }

      this.popularMachineCategoryData = data;
      this.allMachineCategoryData = this.buildTree(types);
    });
  }

  generateImageMapping(categories: string[]): { [key: string]: string } {
    return categories.reduce((mapping, category) => {
      const formattedCategory = category.toLowerCase().replace(/ /g, "_");
      mapping[category] = `${this.baseUrlForIcons}${formattedCategory}.png`;
      return mapping;
    }, {} as { [key: string]: string });
  }

  buildTree(data: InputData): TreeNode[] {
    const root: TreeNode[] = [];

    data.forEach(item => {
      const parts = item.value.split("|");
      let currentLevel = root;

      parts.forEach((part, index) => {
        let existingNode = currentLevel.find(node => node.name === part);

        if (!existingNode) {
          existingNode = {
            name: part,
            count: index === parts.length - 1 ? item.count : 0,
            children: [],
            queryParams: { primaryType: part },
          };
          currentLevel.push(existingNode);
        }

        // Add count only at the leaf level
        if (index === parts.length - 1) {
          existingNode.count = item.count;
        }

        currentLevel = existingNode.children;
      });
    });

    return root;
  }

  machineCardParams(machineCardParams: any) {
    return {
      ...this.queryParams,
      radius: "anywhere",
    };
  }

  getSubParams(category, subCategory) {
    return {
      radius: "anywhere",
    };
  }
}

type TreeNode = {
  name: string;
  count: number;
  children: TreeNode[];
  queryParams: any;
};

type InputData = {
  count: number;
  value: string;
}[];
